import { Box, Typography } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import React, { useContext } from 'react';
import ReactHtmlParser from 'react-html-parser';
import { useAppSelector } from '../reduxhooks';
import { ProductPageContext, VariantSelectionContext } from './ProductPageContext';
import { StarsElementRate } from './ProductDetailShared';

// Product name, price and at-a-glance trust signals (rating, units sold,
// stock state). Required in every product layout — a page without it has
// no name or price — but freely movable.

export interface BlockProductTitlePriceData {
  showRating?: boolean;
  showSoldCount?: boolean;
  showStockBadge?: boolean;
  // Strike-through original price + the "-N%" pill.
  showDiscount?: boolean;
}

function formatRupiah(value: number): string {
  return 'Rp ' + Math.round(value).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

const useStyles = makeStyles((theme: Theme) =>
createStyles({
  root: {
    marginBottom: '18px',
  },
  productName: {
    fontSize: '30px',
    marginBottom: '6px',
    [theme.breakpoints.down('sm')]: {
      fontSize: '22px',
    },
  },
  ratingRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '8px',
    flexWrap: 'wrap',
  },
  ratingMeta: {
    fontFamily: 'Open sans, sans-serif',
    fontSize: '13px',
    color: '#6c6c6c',
    marginBottom: '6px',
  },
  priceBox: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    flexWrap: 'wrap',
    marginBottom: '10px',
  },
  price: {
    fontWeight: 600,
    color: '#121212',
    fontFamily: 'Open sans, sans-serif',
    fontSize: '22px',
    [theme.breakpoints.down('sm')]: {
      fontSize: '20px',
    },
  },
  compareAt: {
    fontWeight: 'normal',
    color: '#8a8a8a',
    fontFamily: 'Open sans, sans-serif',
    fontSize: '15px',
    textDecoration: 'line-through',
  },
  discountPill: {
    fontFamily: 'Inter',
    fontWeight: 600,
    fontSize: '12px',
    color: '#e10000',
    backgroundColor: '#ffe9e9',
    borderRadius: '4px',
    padding: '3px 7px 1px',
  },
  stockBadge: {
    fontWeight: 600,
    color: '#2a2a2a',
    fontFamily: 'Open sans, sans-serif',
    fontSize: '14px',
    marginBottom: '10px',
  },
}),
);

export default function BlockProductTitlePrice({ content }: { content: BlockProductTitlePriceData }) {
  const classes = useStyles();
  const page = useContext(ProductPageContext);
  const displayVariant = useContext(VariantSelectionContext)?.displayVariant;
  // Rating comes from THIS product's reviews. The old page showed a
  // store-wide "category" average here, which said nothing about the item
  // the shopper was actually looking at.
  const reviews = useAppSelector((state) => state.productReviews.reviews);
  const averageStars = useAppSelector((state) => state.productReviews.averageStars);
  const fetchedFor = useAppSelector((state) => state.productReviews.fetchProductId);

  if (!page) return null;

  const {
    showRating = true, showSoldCount = true,
    showStockBadge = true, showDiscount = true,
  } = content ?? {};
  const product = page.product;

  // ProductOnUser pricing convention: discountedPrice === -1 means "no
  // discount", and originalPrice doubles as the selling price.
  //
  // Once the shopper has fully selected a variant, that variant owns the
  // price display; its compareAtPrice plays the strike-through role that
  // originalPrice plays for the base product.
  const hasDiscount = displayVariant
    ? displayVariant.compareAtPrice !== null && displayVariant.compareAtPrice > displayVariant.price
    : product.discountedPrice !== undefined && product.discountedPrice !== -1;
  const price = displayVariant
    ? displayVariant.price
    : hasDiscount ? product.discountedPrice : product.originalPrice;
  const compareAtPrice = displayVariant
    ? displayVariant.compareAtPrice ?? 0
    : product.originalPrice;
  const percentOff = hasDiscount && compareAtPrice > 0
    ? Math.round((1 - price / compareAtPrice) * 100)
    : 0;

  // Only trust the review store once it has loaded THIS product.
  const ratingReady = fetchedFor === page.productId && reviews.length > 0;

  return (
    <Box className={classes.root}>
      <Typography className={classes.productName}>
        <b>{ReactHtmlParser(product.name)}</b>
      </Typography>

      {showRating && ratingReady &&
        <Box className={classes.ratingRow}>
          <StarsElementRate stars={Math.round((averageStars ?? 0) * 10) / 10} withText={false} />
          <Typography className={classes.ratingMeta}>
            {(Math.round((averageStars ?? 0) * 10) / 10)} · {reviews.length} ulasan
            {showSoldCount && !!product.totalSold ? ` · ${product.totalSold} terjual` : ''}
          </Typography>
        </Box>}

      {showRating && !ratingReady && showSoldCount && !!product.totalSold &&
        <Typography className={classes.ratingMeta}>{product.totalSold} terjual</Typography>}

      <Box className={classes.priceBox}>
        <Typography className={classes.price}>{formatRupiah(price)}</Typography>
        {showDiscount && hasDiscount &&
          <Typography className={classes.compareAt}>{formatRupiah(compareAtPrice)}</Typography>}
        {showDiscount && hasDiscount && percentOff > 0 &&
          <Typography className={classes.discountPill}>-{percentOff}%</Typography>}
      </Box>

      {showStockBadge && product.isSoldOut &&
        <Typography className={classes.stockBadge}>STOK HABIS</Typography>}
    </Box>
  );
}
